import { type SupabaseClient } from '@supabase/supabase-js';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';

interface ApiErrorResponse {
  message?: string;
  [key: string]: unknown;
}

// Test utility to simulate different API responses
export const testApiError = async (status: 401 | 500) => {
  const error = new AxiosError<ApiErrorResponse>();
  error.response = {
    data: { message: status === 401 ? 'Unauthorized' : 'Server Error' },
    status,
    statusText: status === 401 ? 'Unauthorized' : 'Server Error',
    headers: {},
    config: {} as any,
  };
  throw error;
};

export const callApi = async (
  client: SupabaseClient,
  url: string,
  options: Omit<AxiosRequestConfig, 'url'> = {},
) => {
  try {
    const {
      data: { session },
    } = await client.auth.getSession();

    const headers = {
      ...options.headers,
      'Content-Type': 'application/json',
      ...(session?.access_token && {
        Authorization: `Bearer ${session.access_token}`,
      }),
    };

    const response = await axios({
      ...options,
      url: `${process.env.REACT_APP_PUBLIC_API_URL}${url}`,
      headers,
    });

    return { data: response.data, error: null };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiErrorResponse>;

      if (axiosError.response) {
        const status = axiosError.response.status;
        const errorData = axiosError.response.data;

        // Add status to the error object for proper handling in React Query
        return {
          data: null,
          error: {
            status,
            message: errorData.message || 'An unexpected error occurred',
            ...errorData,
          },
        };
      }

      // Network errors or other axios errors
      return {
        data: null,
        error: {
          status: 502,
          message: 'Network error or service unavailable',
        },
      };
    }

    // Unknown errors
    return {
      data: null,
      error: {
        status: 502,
        message: 'An unexpected error occurred',
      },
    };
  }
};
